import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const authHeaders = (isFormData = false) => {
  const headers = {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    Lang: i18n.language,
  };
  if (!isFormData) {
    headers["Content-Type"] = "application/json";
  }
  return headers;
};

const toFormData = (payload = {}) => {
  const fd = new FormData();
  Object.entries(payload).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    fd.append(key, String(value));
  });
  return fd;
};

/** API returns roots + flattened duplicates — keep root nodes only when nested children exist. */
export const normalizeChartTree = (data) => {
  if (!Array.isArray(data)) return [];
  const hasNestedRoots = data.some(
    (n) =>
      n &&
      n.parent_id == null &&
      Array.isArray(n.children) &&
      n.children.length > 0
  );
  if (hasNestedRoots) {
    return data.filter((n) => n && n.parent_id == null);
  }
  const roots = data.filter((n) => n && n.parent_id == null);
  return roots.length > 0 ? roots : data;
};

export const fetchChartAccounts = createAsyncThunk(
  "chart_accounts/fetchAll",
  async (arg, { rejectWithValue }) => {
    try {
      const search =
        typeof arg === "string"
          ? arg
          : arg && typeof arg === "object"
            ? arg.search
            : undefined;
      const params = new URLSearchParams();
      if (search != null && String(search).trim() !== "") {
        params.set("search", String(search).trim());
      }
      const qs = params.toString();
      const response = await axios.get(
        `${BASE_URL}/chart-accounts/chart_account${qs ? `?${qs}` : ""}`,
        { headers: authHeaders() }
      );
      return normalizeChartTree(response.data.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed load data"
      );
    }
  }
);

export const fetchArchivedChartAccounts = createAsyncThunk(
  "chart_accounts/archived",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/chart-accounts/deleted`, {
        headers: authHeaders(),
      });
      return normalizeChartTree(response.data.data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed load data"
      );
    }
  }
);

export const fetchMainAccount = createAsyncThunk(
  "chart_accounts/fetchMainAccount",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`${BASE_URL}/settings/main-account`, {
        headers: authHeaders(),
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Failed load data"
      );
    }
  }
);

export const addChartAccounts = createAsyncThunk(
  "chart_accounts/add",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/store`,
        toFormData(userData),
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unknown error occurred"
      );
    }
  }
);

export const createSubAccount = createAsyncThunk(
  "chart_accounts/createSubAccount",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/create-sub`,
        toFormData(userData),
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unknown error occurred"
      );
    }
  }
);

export const moveAccount = createAsyncThunk(
  "chart_accounts/moveAccount",
  async (userData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/move`,
        toFormData(userData),
        {
          headers: authHeaders(true),
          params: {
            account_id: userData.account_id,
            new_parent_id: userData.new_parent_id,
          },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unknown error occurred"
      );
    }
  }
);

export const chartAccountsRecord = createAsyncThunk(
  "chart_accounts/chartAccountsRecord",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${BASE_URL}/chart-accounts/show/${id}`,
        { headers: authHeaders() }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "An unknown error occurred",
      });
    }
  }
);

export const updateChartAccounts = createAsyncThunk(
  "chart_accounts/updateChartAccounts",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/update`,
        toFormData({ account_id: id, ...formData }),
        { headers: authHeaders(true) }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "An unknown error occurred"
      );
    }
  }
);

export const deleteChartAccounts = createAsyncThunk(
  "chart_accounts/deleteChartAccounts",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/delete`,
        null,
        {
          headers: authHeaders(),
          params: { account_id: id },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message:
          error.response?.data?.message || "Failed to delete chart accounts",
      });
    }
  }
);

export const restoreRecord = createAsyncThunk(
  "chart_accounts/restoreRecord",
  async (id, { rejectWithValue }) => {
    try {
      const accountId =
        typeof id === "object" && id !== null ? id.id ?? id.account_id : id;
      const response = await axios.post(
        `${BASE_URL}/chart-accounts/restore`,
        null,
        {
          headers: authHeaders(),
          params: { account_id: accountId },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || "restore failed",
      });
    }
  }
);

const ChartAccountsSlice = createSlice({
  name: "chart_accounts",
  initialState: {
    chart_accounts: [],
    main_account: [],
    archived: [],
    record: null,
    isLoading: false,
    isRecordLoading: false,
    error: null,
    success: null,
    errorSub: null,
    successSub: null,
    errorMove: null,
    successMove: null,
    deleteSuccess: null,
    deleteError: null,
    restoreSuccess: null,
    restoreError: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
      state.errorSub = null;
      state.successSub = null;
      state.errorMove = null;
      state.successMove = null;
      state.deleteSuccess = null;
      state.deleteError = null;
      state.restoreSuccess = null;
      state.restoreError = null;
    },
    clearRecord: (state) => {
      state.record = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchChartAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchChartAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.chart_accounts = Array.isArray(action.payload)
          ? action.payload
          : [];
      })
      .addCase(fetchChartAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(fetchArchivedChartAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchArchivedChartAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.archived = Array.isArray(action.payload) ? action.payload : [];
      })
      .addCase(fetchArchivedChartAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(fetchMainAccount.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchMainAccount.fulfilled, (state, action) => {
        state.isLoading = false;
        state.main_account = action.payload;
      })
      .addCase(fetchMainAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to load data";
      })
      .addCase(addChartAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addChartAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addChartAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload || "Failed to add data";
      })
      .addCase(createSubAccount.pending, (state) => {
        state.isLoading = true;
        state.errorSub = null;
      })
      .addCase(createSubAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.successSub = true;
      })
      .addCase(createSubAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.errorSub = action.payload || "Failed to add data";
      })
      .addCase(moveAccount.pending, (state) => {
        state.isLoading = true;
        state.errorMove = null;
      })
      .addCase(moveAccount.fulfilled, (state) => {
        state.isLoading = false;
        state.successMove = true;
      })
      .addCase(moveAccount.rejected, (state, action) => {
        state.isLoading = false;
        state.errorMove = action.payload || "Failed to move data";
      })
      .addCase(chartAccountsRecord.pending, (state) => {
        state.isRecordLoading = true;
        state.error = null;
      })
      .addCase(chartAccountsRecord.fulfilled, (state, action) => {
        state.isRecordLoading = false;
        state.record = action.payload.data;
      })
      .addCase(chartAccountsRecord.rejected, (state, action) => {
        state.isRecordLoading = false;
        state.error =
          action.payload?.message || action.error?.message || "Failed to load";
      })
      .addCase(updateChartAccounts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateChartAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(updateChartAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message ||
          action.payload ||
          "Failed to update record";
      })
      .addCase(deleteChartAccounts.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteChartAccounts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.deleteSuccess =
          action.payload?.message || "Deleted successfully";
      })
      .addCase(deleteChartAccounts.rejected, (state, action) => {
        state.isLoading = false;
        state.deleteError =
          action.payload?.message ||
          action.payload ||
          action.error?.message ||
          "Failed to delete chart accounts";
      })
      .addCase(restoreRecord.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(restoreRecord.fulfilled, (state, action) => {
        state.isLoading = false;
        state.restoreSuccess = action.payload.message;
      })
      .addCase(restoreRecord.rejected, (state, action) => {
        state.isLoading = false;
        state.restoreError =
          action.payload?.message || action.payload || "restore failed";
      });
  },
});

export const { clearState, clearRecord } = ChartAccountsSlice.actions;
export default ChartAccountsSlice.reducer;
